/**
 * story-viewer.tsx
 *
 * Exports two components:
 *
 *  StoryViewer       — single-profile viewer; simple API, no cross-profile nav.
 *                      Used by profile cards and the profile detail header.
 *
 *  MultiStoryViewer  — multi-profile viewer with cross-profile navigation and
 *                      ad-slide injection between profiles.
 *                      Used by the homepage StoriesRow.
 */
import { useTouchDevice } from '@/hooks/use-touch-device';
import type { GalleryPhoto } from '@/types';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight, ExternalLink, Heart, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface StoryViewerProfile {
    id: string | number;
    slug?: string;
    display_name: string;
    avatar?: { original_url: string };
    stories: GalleryPhoto[];
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

export const STORY_DURATION_MS = 5_000;

function timeAgo(dateStr: string | null | undefined): string | null {
    if (!dateStr) return null;
    const ms = Date.now() - new Date(dateStr).getTime();
    const sec = Math.floor(ms / 1000);
    if (sec < 60) return 'just now';
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day}d ago`;
    return new Date(dateStr).toLocaleDateString();
}

function profileHref(profile: StoryViewerProfile): string {
    return profile.slug ? `/escort/${profile.slug}` : `/p/${profile.id}`;
}

// ---------------------------------------------------------------------------
// Shared hooks
// ---------------------------------------------------------------------------

export function useLikes() {
    const [likedIds, setLikedIds] = useState<Set<string>>(() => {
        try {
            return new Set(JSON.parse(localStorage.getItem('story_likes') ?? '[]') as string[]);
        } catch {
            return new Set<string>();
        }
    });

    const toggle = useCallback((id: string | number) => {
        setLikedIds((prev) => {
            const next = new Set(prev);
            const key = String(id);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            try {
                localStorage.setItem('story_likes', JSON.stringify([...next]));
            } catch {}
            return next;
        });
    }, []);

    const isLiked = useCallback((id: string | number) => likedIds.has(String(id)), [likedIds]);

    return { isLiked, toggle };
}

export function useOpenPhase(open: boolean) {
    const [phase, setPhase] = useState<'closed' | 'opening' | 'open' | 'closing'>('closed');

    useEffect(() => {
        if (open) {
            // 'opening' = rendered but invisible; double-rAF guarantees the browser
            // paints that frame before we switch to 'open', so the CSS transition plays.
            setPhase('opening');
            let raf1: number;
            let raf2: number;
            raf1 = requestAnimationFrame(() => {
                raf2 = requestAnimationFrame(() => setPhase('open'));
            });
            return () => {
                cancelAnimationFrame(raf1);
                cancelAnimationFrame(raf2);
            };
        } else if (phase !== 'closed') {
            setPhase('closing');
            // 310ms > duration-300 so the element unmounts after the transition finishes
            const id = setTimeout(() => setPhase('closed'), 310);
            return () => clearTimeout(id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return phase;
}

export function useBodyScrollLock(open: boolean) {
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

export interface ProfileHeaderProps {
    profile: StoryViewerProfile;
    onClose: () => void;
}

export function ProfileHeader({ profile, onClose }: ProfileHeaderProps) {
    const { t } = useTranslation('common');
    return (
        <div className="absolute top-4 right-0 left-0 z-10 flex items-center justify-between gap-2 px-3 pt-3">
            <div className="flex min-w-0 items-center gap-2">
                {profile.avatar?.original_url && (
                    <img
                        src={profile.avatar.original_url}
                        alt={profile.display_name}
                        className="h-8 w-8 shrink-0 rounded-full border border-white/20 object-cover"
                        draggable={false}
                    />
                )}
                <span className="truncate text-sm font-semibold text-white drop-shadow-sm">
                    {profile.display_name}
                </span>
            </div>
            <button
                className="shrink-0 rounded-full bg-black/40 p-1.5 text-white backdrop-blur-sm"
                onClick={onClose}
                aria-label={t('actions.close')}
            >
                <X className="h-4 w-4" />
            </button>
        </div>
    );
}

export interface StoryImageProps {
    story: GalleryPhoto;
    index: number;
}

export function StoryImage({ story, index }: StoryImageProps) {
    return (
        <div className="relative h-full w-full shrink-0">
            <img
                src={story.thumb ?? story.url}
                alt=""
                aria-hidden
                className="absolute inset-0 h-full w-full scale-110 object-cover blur-2xl brightness-40"
                draggable={false}
            />
            <img
                src={story.url}
                alt={`Story ${index + 1}`}
                className="relative h-full w-full object-contain"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
                style={{ WebkitTouchCallout: 'none' }}
            />
        </div>
    );
}

export interface BottomOverlayProps {
    story: GalleryPhoto;
    profile: StoryViewerProfile;
    isLiked: (id: string | number) => boolean;
    toggleLike: (id: string | number) => void;
}

export function BottomOverlay({ story, profile, isLiked, toggleLike }: BottomOverlayProps) {
    const { t } = useTranslation('common');
    const ago = timeAgo(story.created_at);
    const href = profileHref(profile);

    return (
        <div className="pointer-events-none absolute right-0 bottom-0 left-0 z-10 flex flex-col gap-3 bg-gradient-to-t from-black/70 to-transparent px-4 pb-6 pt-20">
            {ago && <span className="text-xs text-white/60">{ago}</span>}
            <div className="pointer-events-auto flex items-center justify-between gap-3">
                {story.id != null && (
                    <button
                        type="button"
                        className="flex items-center gap-1.5 rounded-full bg-black/40 px-3 py-1.5 backdrop-blur-sm"
                        aria-label={isLiked(story.id) ? t('a11y.unlike_story') : t('a11y.like_story')}
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleLike(story.id!);
                        }}
                    >
                        <Heart
                            className={`h-4 w-4 transition-colors ${
                                isLiked(story.id) ? 'fill-red-500 text-red-500' : 'text-white'
                            }`}
                        />
                        <span className="text-xs font-medium text-white">
                            {isLiked(story.id) ? t('a11y.unlike_story') : t('a11y.like_story')}
                        </span>
                    </button>
                )}
                <a
                    href={href}
                    className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground shadow-lg"
                    onClick={(e) => e.stopPropagation()}
                >
                    <ExternalLink className="h-3.5 w-3.5" />
                    {profile.display_name}
                </a>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// StoryViewer — single profile
// ---------------------------------------------------------------------------

interface StoryViewerProps {
    profile: StoryViewerProfile;
    open: boolean;
    onClose: () => void;
}

export function StoryViewer({ profile, open, onClose }: StoryViewerProps) {
    const { t } = useTranslation('common');
    const isTouch = useTouchDevice();
    const { isLiked, toggle: toggleLike } = useLikes();
    const phase = useOpenPhase(open);
    useBodyScrollLock(open);

    const stories = profile.stories;

    const [emblaRef, emblaApi] = useEmblaCarousel({
        axis: 'x',
        startIndex: 0,
        watchDrag: isTouch,
        loop: false,
        align: 'center',
    });

    const [current, setCurrent] = useState(0);
    const [progress, setProgress] = useState(0);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const startTimeRef = useRef<number>(0);

    // Reset when reopened
    useEffect(() => {
        if (!open || !emblaApi) return;
        emblaApi.scrollTo(0, true);
        setCurrent(0);
        setProgress(0);
    }, [open, emblaApi]);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setCurrent(emblaApi.selectedScrollSnap());
        setProgress(0);
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        emblaApi.on('select', onSelect);
        return () => {
            emblaApi.off('select', onSelect);
        };
    }, [emblaApi, onSelect]);

    const goNext = useCallback(() => {
        if (!emblaApi) return;
        if (current >= stories.length - 1) {
            onClose();
            return;
        }
        emblaApi.scrollNext();
    }, [emblaApi, current, stories.length, onClose]);

    const goPrev = useCallback(() => {
        if (!emblaApi) return;
        if (current <= 0) {
            onClose();
            return;
        }
        emblaApi.scrollPrev();
    }, [emblaApi, current, onClose]);

    // Auto-advance
    const startTimer = useCallback(() => {
        if (timerRef.current) clearInterval(timerRef.current);
        startTimeRef.current = Date.now();
        timerRef.current = setInterval(() => {
            const elapsed = Date.now() - startTimeRef.current;
            setProgress(Math.min((elapsed / STORY_DURATION_MS) * 100, 100));
            if (elapsed >= STORY_DURATION_MS) goNext();
        }, 50);
    }, [goNext]);

    useEffect(() => {
        if (!open || phase === 'closed') {
            if (timerRef.current) clearInterval(timerRef.current);
            return;
        }
        startTimer();
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [open, current, phase, startTimer]);

    // Keyboard
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (!open) return;
            if (e.key === 'ArrowRight') goNext();
            if (e.key === 'ArrowLeft') goPrev();
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, goNext, goPrev, onClose]);

    // Swipe-down to close
    const touchStartY = useRef<number>(0);
    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        touchStartY.current = e.touches[0].clientY;
    }, []);
    const handleTouchEnd = useCallback(
        (e: React.TouchEvent) => {
            if (e.changedTouches[0].clientY - touchStartY.current > 80) onClose();
        },
        [onClose],
    );

    if (phase === 'closed') return null;

    const isVisible = phase === 'open';
    const currentStory = stories[current];

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center transition-all duration-300"
            style={{
                backgroundColor: isVisible ? 'rgba(0,0,0,0.92)' : 'rgba(0,0,0,0)',
                backdropFilter: isVisible ? 'blur(4px)' : 'blur(0px)',
            }}
            onClick={onClose}
        >
            <div
                className="relative h-full max-h-[100dvh] w-full max-w-md overflow-hidden bg-black transition-all duration-300 md:max-h-[88vh] md:max-w-[390px] md:rounded-2xl"
                style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'scale(1) translateY(0)' : 'scale(0.96) translateY(16px)',
                }}
                onClick={(e) => e.stopPropagation()}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            >
                {/* Progress bars */}
                <div className="absolute top-0 right-0 left-0 z-10 flex gap-1 p-2">
                    {stories.map((_, i) => (
                        <div key={i} className="h-0.5 flex-1 overflow-hidden rounded-full bg-white/30">
                            <div
                                className="h-full bg-white transition-none"
                                style={{
                                    width: i < current ? '100%' : i === current ? `${progress}%` : '0%',
                                }}
                            />
                        </div>
                    ))}
                </div>

                <ProfileHeader profile={profile} onClose={onClose} />

                {/* Embla viewport */}
                <div ref={emblaRef} className="h-full w-full overflow-hidden">
                    <div className="flex h-full">
                        {stories.map((story, i) => (
                            <StoryImage key={story.id ?? i} story={story} index={i} />
                        ))}
                    </div>
                </div>

                {currentStory && (
                    <BottomOverlay
                        story={currentStory}
                        profile={profile}
                        isLiked={isLiked}
                        toggleLike={toggleLike}
                    />
                )}

                {/* Desktop tap zones */}
                {!isTouch && (
                    <>
                        <button
                            className="absolute inset-y-0 left-0 w-1/3 cursor-pointer"
                            onClick={goPrev}
                            aria-label={t('a11y.prev_story')}
                        />
                        <button
                            className="absolute inset-y-0 right-0 w-1/3 cursor-pointer"
                            onClick={goNext}
                            aria-label={t('a11y.next_story')}
                        />
                    </>
                )}

                {/* Desktop arrow hints */}
                {!isTouch && current > 0 && (
                    <div className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2">
                        <ChevronLeft className="h-8 w-8 text-white/50" />
                    </div>
                )}
                {!isTouch && current < stories.length - 1 && (
                    <div className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2">
                        <ChevronRight className="h-8 w-8 text-white/50" />
                    </div>
                )}
            </div>
        </div>
    );
}

// MultiStoryViewer moved to multi-story-viewer.tsx

// kept for back-compat — consumers should import from multi-story-viewer.tsx directly
export type { MultiStoryViewerProps } from './multi-story-viewer';
export { MultiStoryViewer } from './multi-story-viewer';

